import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { space } from '@design-system/tokens';
import { bgColorsVars } from '@design-system/vars.css';
import { Autocomplete, Box, TextField } from '@mui/material';
import { forwardRef, useMemo } from 'react';
import { Option } from '../../view-model/discounts.mappers';

export type SelectProps<T extends string | number> = {
  options: Array<Option<T>>;
  value?: T | null;
  name: string;
  onChange: (v: React.ChangeEvent<any>) => void;
  error?: string;
  width?: number;
  label?: string;
};

export const FormSelect = forwardRef(function FormSelect<
  T extends string | number,
>(
  { error, width, label, ...p }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const value = useMemo(
    () => p.options.find((v) => v.value === p.value),
    [p.options, p.value],
  );

  return (
    <Col style={{ gap: 0, ...(width ? { width: width + 'px' } : {}) }}>
      {label && (
        <Typography
          variant="body"
          size="medium"
          fontWeight="medium"
          style={{ marginBottom: space.s }}
        >
          {label}
        </Typography>
      )}
      <Row gap="s">
        <div className="flex flex-col w-full">
          <Autocomplete
            options={p.options}
            ref={ref}
            sx={{
              width: '100%',
              '& .MuiInputBase-root': {
                paddingLeft: '12px',
                borderRadius: '10px',
                borderWidth: '2px',
                height: '40px',
                borderColor: error
                  ? bgColorsVars['ERROR-100']
                  : bgColorsVars['N-40'],
                boxShadow: 'none',
              },
              '& .Mui-focused': {
                borderRadius: '10px',
                borderWidth: '2px',
                height: '40px',
                borderColor: error
                  ? bgColorsVars['ERROR-100']
                  : bgColorsVars['N-100'],
                boxShadow: 'none',
              },
              '& .MuiInputBase-input': {
                fontSize: '16px',
                fontWeight: 500,
                fontFamily: 'Visuelt',
              },
              '& .MuiInputBase-input::placeholder': {
                color: bgColorsVars['N-100'],
                opacity: 1,
                fontSize: '16px',
                fontWeight: 500,
                fontFamily: 'Visuelt',
              },
            }}
            value={value}
            onChange={(event, value) =>
              p.onChange({ ...event, target: { ...event.target, value } })
            }
            renderInput={(params) => (
              <TextField {...params} placeholder="Select" />
            )}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              return (
                <Box key={key} {...optionProps} component="li">
                  <div className="w-full flex justify-between items-center">
                    <Typography variant="body" size="large">
                      {option.label}
                    </Typography>
                    {option?.type && (
                      <Typography variant="body" size="small" color="N-60">
                        {option.type}
                      </Typography>
                    )}
                  </div>
                </Box>
              );
            }}
          />
          {error && (
            <Typography
              variant="body"
              size="small"
              color="ERROR-100"
              className="py-1 px-4"
            >
              {error}
            </Typography>
          )}
        </div>
      </Row>
    </Col>
  );
});
